import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { GenericTooltip, InfoCircle } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  CLICKS_AND_VIEWS_ENHANCED_MODEL,
  useUpsellEnhancedModel,
} from "@features/clicks-and-view-enhanced-modelling/use-upsell-enhanced-model";
import {
  type IMCAttributionWindowDays,
  type ITimeGranularity,
} from "@north-beam/nb-common";
import {
  toClicksAndViewsLabel,
  useAttributionWindowOptions,
} from "@pages/sales/report-body-control/attribution-window-select";
import { Select } from "@shared/selects";

import { Chip } from "./buttons/chip";

interface SelectOption {
  value: string;
  label: string;
}
interface Props {
  timeGranularity: ITimeGranularity;
  isAttributionWindowVisible?: boolean;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  label?: string;
  disabled?: boolean;
  onAttributionModelChange: (value: string) => void;
  onAttributionWindowChange: (value: IMCAttributionWindowDays) => void;
  options?: SelectOption[];
  attributionWindowOptionsFilter?: (option: SelectOption) => boolean;
}

export const AttributionModelAndWindowSelect = ({
  label = "ATTRIBUTION MODEL & WINDOW",
  timeGranularity,
  isAttributionWindowVisible = true,
  attributionModel,
  attributionWindow: selectedAttributionWindow,
  disabled,
  onAttributionModelChange,
  onAttributionWindowChange,
  options,
  attributionWindowOptionsFilter = () => true,
}: Props) => {
  const { user } = useUser();

  const amEnum = user.attributionMethodEnum;

  const { partnersForEnhancedModelLogosWithOverlay, tryEnhancedModelCard } =
    useUpsellEnhancedModel();

  const { options: initialOptions, reconciledAttributionWindow } =
    useAttributionWindowOptions({
      timeGranularity,
      selectedAttributionWindow,
    });
  let attributionWindowOptions = initialOptions;

  if (attributionModel === "northbeam_custom__va") {
    attributionWindowOptions = attributionWindowOptions.map(
      toClicksAndViewsLabel,
    );
  }

  const selectOptions = options ?? amEnum.reactSelectOptions;

  const hasEnhancedModelInOptions = selectOptions.some(
    (option) => option.value === CLICKS_AND_VIEWS_ENHANCED_MODEL,
  );

  const getAttributionModelOptionClassName = ({
    isActive,
    isSelected,
    isDisabled,
  }: {
    isActive: boolean;
    isSelected: boolean;
    isDisabled?: boolean;
  }) =>
    twMerge(
      isActive && "bg-cosmic-5",
      isSelected && !isDisabled
        ? "bg-v2-highlight-information font-semibold"
        : "hover:bg-v2-secondary font-normal",
      isDisabled && "bg-gray-3 pointer-events-none",
      "px-3 py-2 cursor-pointer text-v2-primary flex hover:font-semibold",
    );

  return (
    <div
      className={clsx("text-v2-primary text-sm", {
        "pointer-events-none": disabled,
      })}
    >
      <label
        className={clsx(
          "label text-xs leading-5 flex gap-1 mb-[3px]",
          disabled && "text-disabled",
        )}
      >
        {label}
        <GenericTooltip
          content={
            <div className="w-[600px]">
              <div className="flex gap-12 mb-2 font-bold tracking-wide">
                <div className="flex-1">Attribution Model</div>
                <div className="flex-1">Attribution Window</div>
              </div>
              <div className="flex gap-12 mb-2">
                <div className="flex-1">
                  Attribution models allow you to choose the method by which
                  each ad interaction receives credit for your conversions.
                </div>
                <div className="flex-1">
                  An attribution window is the period of time after an ad
                  interaction (such as an ad click or video view) during which a
                  conversion, such as a purchase, is recorded in the Northbeam
                  system.
                </div>
              </div>
              <div className="flex gap-12">
                <div className="flex-1">
                  <a
                    rel="noreferrer"
                    href="https://docs.northbeam.io/docs/attribution-models"
                    target="_blank"
                    className={clsx(
                      "-ml-2 bottom-0 no-underline hover:bg-cosmic-4",
                      "hover:text-v2-action rounded-full py-1 px-2 font-medium w-fit max-w-full",
                    )}
                  >
                    Click here to learn more
                  </a>
                </div>
                <div className="flex-1">
                  <a
                    rel="noreferrer"
                    href="https://docs.northbeam.io/docs/attribution-windows"
                    target="_blank"
                    className={clsx(
                      "-ml-2 bottom-0 no-underline hover:bg-cosmic-4",
                      "hover:text-v2-action rounded-full py-1 px-2 font-medium w-fit max-w-full",
                    )}
                  >
                    Click here to learn more
                  </a>
                </div>
              </div>
            </div>
          }
        />
      </label>
      <div className={clsx("flex", { "opacity-60": disabled })}>
        <div
          className={clsx({
            "min-w-[140px]": isAttributionWindowVisible,
            "min-w-[240px]": !isAttributionWindowVisible,
          })}
        >
          <Select
            className={clsx(
              "relative bg-v2-primary text-v2-primary border-v2-secondary hover:z-[9]",
              {
                "rounded-r-none": isAttributionWindowVisible,
              },
            )}
            activeInputClassName="border border-v2-action ring-1 ring-cosmic z-[9]"
            value={attributionModel}
            options={selectOptions}
            menuClassName={
              hasEnhancedModelInOptions ? "w-[20rem]" : "w-[18rem]"
            }
            onChange={onAttributionModelChange}
            menuItemRender={({ option, active, selected }) =>
              option.value === CLICKS_AND_VIEWS_ENHANCED_MODEL ? (
                <div>
                  <GenericTooltip
                    v2Styling
                    overlayClassName="p-0 rounded-md shadow"
                    content={tryEnhancedModelCard}
                    placement="right"
                    noInfoCircle
                  >
                    <div
                      className={getAttributionModelOptionClassName({
                        isActive: active,
                        isSelected: selected,
                        isDisabled: option.disabled,
                      })}
                    >
                      <span className="flex flex-grow items-center">
                        <Chip className="mr-2 bg-venus+0" text="NEW" />
                        <div className="mr-2 flex-grow">{option.label}</div>
                        {partnersForEnhancedModelLogosWithOverlay}{" "}
                      </span>
                      <InfoCircle v2Styling />
                    </div>
                  </GenericTooltip>
                </div>
              ) : (
                <div
                  className={getAttributionModelOptionClassName({
                    isActive: active,
                    isSelected: selected,
                    isDisabled: option.disabled,
                  })}
                >
                  <div>{option.label}</div>
                </div>
              )
            }
          />
        </div>
        {isAttributionWindowVisible && (
          <div className="min-w-[100px]">
            <Select
              className="ml-[-1px] relative rounded-l-none bg-v2-primary text-v2-primary border-v2-secondary"
              activeInputClassName="border border-v2-action ring-1 ring-cosmic z-[9]"
              value={reconciledAttributionWindow}
              options={
                attributionWindowOptionsFilter
                  ? attributionWindowOptions.filter(
                      attributionWindowOptionsFilter,
                    )
                  : attributionWindowOptions
              }
              onChange={onAttributionWindowChange}
              name="attribution-window"
            />
          </div>
        )}
      </div>
    </div>
  );
};
